import { useQuery } from '@tanstack/react-query';
import { apiUrlGenerator } from 'infrastructure/hooks/use-api';
import dataService from 'services/dataService';
import { API_PATIENT_DIAGNOSES } from 'services/dataService/ccmResources';
import { showResult } from 'infrastructure/hooks/utils';
import { STALE_TIME } from 'infrastructure/consts/stale-time';

import type { SortState } from 'infrastructure/types';
import type {
  IPartialPaginationMeta,
  IConditionResponse,
} from 'infrastructure/interfaces';

interface Props {
  patientGuid: string;
  meta: IPartialPaginationMeta;
  enable?: boolean;
  documentationTimeSort?: SortState;
  dateTimeSort?: SortState;
}

type TFilter = {
  patientGuid: string;
  startTime?: string;
  endTime?: string;
  documentationTimeSort?: string;
  updatedAtSort?: string;
} & IPartialPaginationMeta;

export const getConditionsFn = async ({ ...filter }: TFilter) => {
  const url = API_PATIENT_DIAGNOSES;
  const { data, error } = await dataService.getList<IConditionResponse>(
    apiUrlGenerator(url, filter),
  );

  if (error) {
    showResult(error);
    throw new Error(error);
  }

  return data;
};

export const getConditionsKey = (filter: TFilter) => ['getConditions', filter];

export const CONDITIONS_PER_PAGE = 5;

export const useGetConditions = ({
  patientGuid,
  meta = {},
  enable = true,
  documentationTimeSort,
  dateTimeSort,
}: Props) => {
  const filter: TFilter = {
    ...meta,
    patientGuid,
    documentationTimeSort: documentationTimeSort?.toUpperCase(),
    updatedAtSort: dateTimeSort?.toUpperCase(),
  };

  const {
    data: goals,
    isLoading,
    isFetching,
    isRefetching,
    ...other
  } = useQuery({
    queryKey: getConditionsKey(filter),
    queryFn: async () => getConditionsFn(filter),
    staleTime: STALE_TIME,
    enabled: enable,
  });

  return {
    goals,
    isLoading: isLoading || isFetching || isRefetching,
    isFetching,
    ...other,
  };
};
